﻿import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { MessageSquare, X, Send, Sparkles, Loader2, Bot, Settings, Mic, MicOff, HelpCircle, ChevronRight, GripVertical, Camera, Languages, FileText, BookmarkPlus, MessageSquarePlus } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { GlassCard } from './SharedUI';
import { useSocket } from '@/hooks/useSocket';
import { useTTS } from '@/hooks/useTTS';
import { useApp } from '@/contexts/AppContext';
import { requestMicrophoneStream } from '@/services/sensorPermissionService';
import Markdown from 'react-markdown';

/* ── 位置系统：自由拖动 + 四角吸附 + localStorage 持久化 ── */
const SNAP_THRESHOLD = 80;
const SNAP_ANIM_MS = 200;
const STORAGE_KEY = 'xf_floating_manual_pos';
const LONG_PRESS_MS = 500;

interface SavedPos { x: number; y: number; }

function loadPos(): SavedPos | null {
  try { const raw = localStorage.getItem(STORAGE_KEY); if (raw) return JSON.parse(raw); } catch {}
  return null;
}
function savePos(pos: SavedPos) { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(pos)); } catch {} }

/* ── 页面环境检测 ── */
type PageContext = 'chat' | 'knowledge' | 'personality' | 'settings' | 'default';

interface PageTheme {
  bg: string;          // 按钮背景 Tailwind 类
  glow: string;        // 按钮光晕 Tailwind 类
  border: string;      // 面板边框 Tailwind 类
  iconBg: string;      // 面板头部图标背景
  accentText: string;  // 强调文字颜色
}

const PAGE_THEMES: Record<PageContext, PageTheme> = {
  chat:        { bg: 'bg-celestial-saturn', glow: 'shadow-[0_8px_25px_rgba(255,204,0,0.3)]', border: 'border-celestial-saturn/30', iconBg: 'bg-celestial-saturn', accentText: 'text-celestial-saturn' },
  knowledge:   { bg: 'bg-indigo-500',      glow: 'shadow-[0_8px_25px_rgba(99,102,241,0.35)]',   border: 'border-indigo-400/30',   iconBg: 'bg-indigo-500',      accentText: 'text-indigo-400' },
  personality: { bg: 'bg-violet-500',      glow: 'shadow-[0_8px_25px_rgba(168,85,247,0.35)]',   border: 'border-violet-400/30',   iconBg: 'bg-violet-500',      accentText: 'text-violet-400' },
  settings:    { bg: 'bg-gray-500',        glow: 'shadow-[0_8px_25px_rgba(156,163,175,0.25)]',   border: 'border-gray-400/30',     iconBg: 'bg-gray-500',        accentText: 'text-gray-400' },
  default:     { bg: 'bg-celestial-saturn', glow: 'shadow-[0_8px_25px_rgba(255,204,0,0.3)]', border: 'border-celestial-saturn/30', iconBg: 'bg-celestial-saturn', accentText: 'text-celestial-saturn' },
};

function usePageContext(): { page: PageContext; prefill: string } {
  const [page, setPage] = useState<PageContext>('default');

  useEffect(() => {
    const detect = () => {
      const hash = window.location.hash;
      // 从路由 hash 判断当前页面
      if (hash.includes('/chat') || hash.includes('/agent')) setPage('chat');
      else if (hash.includes('/knowledge')) setPage('knowledge');
      else if (hash.includes('/personality')) setPage('personality');
      else if (hash.includes('/settings')) setPage('settings');
      else setPage('default');
    };
    detect();
    window.addEventListener('hashchange', detect);
    return () => window.removeEventListener('hashchange', detect);
  }, []);

  // 尝试获取当前页面的上下文预填内容
  const prefill = useMemo(() => {
    try {
      // 选中文字
      const sel = window.getSelection()?.toString().trim();
      if (sel && sel.length > 0 && sel.length < 200) return sel;
    } catch {}
    return '';
  }, [page]);

  return { page, prefill };
}

/* ── 轮盘菜单项 ── */
interface RadialItem {
  id: string;
  icon: React.ReactNode;
  label: string;
  color: string;
}

const RADIAL_ITEMS: RadialItem[] = [
  { id: 'screenshot',  icon: <Camera size={18} />,       label: '截图提问',     color: '#6366F1' },
  { id: 'translate',   icon: <Languages size={18} />,    label: '翻译选中',     color: '#8B5CF6' },
  { id: 'summarize',   icon: <FileText size={18} />,     label: '总结内容',     color: '#EC4899' },
  { id: 'note',        icon: <BookmarkPlus size={18} />, label: '记录记忆',     color: '#F59E0B' },
  { id: 'newchat',     icon: <MessageSquarePlus size={18} />, label: '新建对话', color: '#10B981' },
  { id: 'voice',       icon: <Mic size={18} />,          label: '语音输入',     color: '#EF4444' },
];

export function FloatingAgent({ t }: { t: any }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([
    { role: 'assistant', content: t.manualIntro || '你好！我是 小枫 使用说明书助手。有什么我可以帮您了解平台的吗？' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const { speak, isSpeaking } = useTTS();
  const { autoSpeakReply } = useApp();
  const scrollRef = useRef<HTMLDivElement>(null);
  const socket = useSocket();

  /* ── 页面感知 ── */
  const { page, prefill } = usePageContext();
  const theme = PAGE_THEMES[page];

  /* ── 拖动状态 ── */
  const containerRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<SavedPos>(() => {
    const saved = loadPos();
    if (saved) return saved;
    return { x: window.innerWidth - 80, y: window.innerHeight - 80 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef<{ mx: number; my: number; px: number; py: number } | null>(null);
  const [dragTooltip, setDragTooltip] = useState(() => !loadPos());

  /* ── 长按轮盘状态 ── */
  const [showRadial, setShowRadial] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pointerStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const isLongPress = useRef(false);
  const [hoveredRadial, setHoveredRadial] = useState<string | null>(null);

  const quickSuggestions = [
    { id: 'create', label: t.howToCreateAgent },
    { id: 'legacy', label: t.whatIsLegacyProtocol },
    { id: 'credits', label: t.howToEarnCredits },
    { id: 'privacy', label: t.privacyPolicy },
  ];

  /* ── 清除长按定时器 ── */
  const clearLongPress = useCallback(() => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
  }, []);

  /* ── toggleListen 前置声明（避免下方 handleRadialAction 依赖数组读取未初始化的 const 触发 TDZ） ── */
  const toggleListen = useCallback(async () => {
    if (isListening) { setIsListening(false); return; }
    try { const stream = await requestMicrophoneStream(true); stream.getTracks().forEach(t => t.stop()); } catch (err) { console.error('Mic denied:', err); return; }
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.lang = 'zh-CN';
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (event: any) => { setInput(event.results[0][0].transcript); setIsListening(false); };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognition.start();
  }, [isListening]);

  /* ── 轮盘各动作处理 ── */
  const handleRadialAction = useCallback((actionId: string) => {
    setShowRadial(false);
    setIsOpen(true);

    switch (actionId) {
      case 'screenshot': {
        // 提示用户粘贴截图或描述内容
        setInput('');
        setTimeout(() => setInput('请帮我分析截图内容：'), 50);
        break;
      }
      case 'translate': {
        const sel = window.getSelection()?.toString().trim();
        if (sel) {
          setMessages(prev => [...prev, { role: 'assistant', content: sel }]);
          handleSendRef.current?.(`请将以下文字翻译成中文：\n${sel}`);
        } else {
          setMessages(prev => [...prev, { role: 'assistant', content: '请先选中要翻译的文字,然后再次点击翻译' }]);
        }
        break;
      }
      case 'summarize': {
        // 尝试获取页面主旨
        const title = document.title || '';
        const url = window.location.href;
        setMessages(prev => [...prev, { role: 'assistant', content: `请总结当前页面：${title} (${url})` }]);
        handleSendRef.current?.(`请总结当前页面"${title}"的主要内容`);
        break;
      }
      case 'note': {
        setInput('记住：');
        break;
      }
      case 'newchat': {
        // 跳转到对话页
        window.location.hash = '#/chat';
        break;
      }
      case 'voice': {
        toggleListen();
        break;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListening, toggleListen]);

  /* ── 拖动手势 ── */
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    // Allow clicks on radial items to pass through
    if (target.closest('[data-radial-item]')) return;
    if (!target.closest('[data-drag-handle]')) return;
    e.preventDefault();

    pointerStartRef.current = { x: e.clientX, y: e.clientY };
    isLongPress.current = false;

    // 开始拖动
    dragStart.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y };
    setIsDragging(true);
    setDragTooltip(false);

    // 长按检测（如果松手时没有移动太多，触发轮盘）
    clearLongPress();
    longPressTimer.current = setTimeout(() => {
      // 检查是否几乎没有移动
      const dx = Math.abs(e.clientX - pointerStartRef.current.x);
      const dy = Math.abs(e.clientY - pointerStartRef.current.y);
      if (dx < 10 && dy < 10) {
        isLongPress.current = true;
        setIsDragging(false);
        dragStart.current = null;
        setShowRadial(true);
      }
    }, LONG_PRESS_MS);
  }, [pos, clearLongPress]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: PointerEvent) => {
      if (!dragStart.current) return;
      const dx = e.clientX - dragStart.current.mx;
      const dy = e.clientY - dragStart.current.my;
      setPos({ x: dragStart.current.px + dx, y: dragStart.current.py + dy });
    };
    const onUp = () => {
      setIsDragging(false);
      dragStart.current = null;
      clearLongPress();
      // Snap to nearest corner
      setPos(prev => {
        const w = window.innerWidth; const h = window.innerHeight;
        let nx = prev.x; let ny = prev.y;
        if (nx < SNAP_THRESHOLD) nx = 24; else if (nx > w - SNAP_THRESHOLD) nx = w - 24;
        if (ny < SNAP_THRESHOLD) ny = 24; else if (ny > h - SNAP_THRESHOLD) ny = h - 24;
        savePos({ x: nx, y: ny });
        return { x: nx, y: ny };
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
  }, [isDragging, clearLongPress]);

  /* ── 窗口缩放 ── */
  useEffect(() => {
    const onResize = () => setPos(prev => {
      const w = window.innerWidth; const h = window.innerHeight;
      return { x: Math.min(Math.max(24, prev.x), w - 24), y: Math.min(Math.max(24, prev.y), h - 24) };
    });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /* ── 轮盘打开时，点击外部关闭 ── */
  useEffect(() => {
    if (!showRadial) return;
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-drag-handle]') || target.closest('[data-radial-item]')) return;
      setShowRadial(false);
    };
    window.addEventListener('pointerdown', onClickOutside);
    return () => window.removeEventListener('pointerdown', onClickOutside);
  }, [showRadial]);

  /* ── 卸载时清理长按定时器，避免组件卸载后定时器仍存活（闭包泄漏） ── */
  useEffect(() => () => clearLongPress(), [clearLongPress]);

  /* ── Socket ── */
  useEffect(() => {
    if (!socket || !isOpen) return;
    const onResponse = (data: { text: string }) => {
      setMessages(prev => [...prev, { role: 'assistant', content: data.text }]);
      if (autoSpeakReply) speak(data.text);
      setIsLoading(false);
    };
    const onStatus = (data: { status: string }) => setIsLoading(data.status === "thinking");
    const onError = (data: { message?: string }) => {
      // v3.12.64: 之前漏监听 agent:error → 服务端 outer catch 发的事件前端无响应
      setIsLoading(false);
      if (data?.message) {
        setMessages(prev => [...prev, { role: 'assistant', content: `⚠ ${data.message}` }]);
      }
    };
    socket.on("agent:response", onResponse);
    socket.on("agent:status", onStatus);
    socket.on("agent:error", onError);
    return () => { socket.off("agent:response", onResponse); socket.off("agent:status", onStatus); socket.off("agent:error", onError); };
  }, [isOpen, socket, speak, autoSpeakReply]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, isLoading]);

  /* ── 用 ref 避免 handleSend 闭包过期 ── */
  const handleSendRef = useRef<(text?: string) => Promise<void>>();

  const handleSend = useCallback(async (text?: string) => {
    const messageToSend = text || input.trim();
    if (!messageToSend || isLoading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: messageToSend }]);
    setIsLoading(true);
    if (socket) {
      socket.emit("agent:chat", {
        text: messageToSend,
        history: messages.map(m => ({ role: m.role, content: m.content })),
        personalityId: 'xiaofeng',
        requestId: 'fa-' + Date.now(),
        source: 'floating-agent',
      });
    }
  }, [input, isLoading, messages, socket]);

  handleSendRef.current = handleSend;

  /* ── 智能预填 placeholder ── */
  const smartPlaceholder = useMemo(() => {
    if (isListening) return t.listening || '聆听中...';
    if (prefill) return `关于"${prefill.slice(0, 30)}${prefill.length > 30 ? '...' : ''}"...`;
    return t.askAboutLumiAI || '询问小枫OS的功能...';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isListening, prefill, page]);

  /* ── 计算轮盘按钮的屏幕坐标 ── */
  const radialAngles = useMemo(() => {
    // 6 个按钮按垂直曲线排列（中间项最左、上下两端稍近），确保互不遮挡
    // 选用 4px 宽 4 字符 label + 48px 按钮 = 约 56px 实际占用，间距 75px
    const positions: { x: number; y: number; deg: number }[] = [
      { x:  -85, y: -180, deg: 0 },  // 语音输入 (top)
      { x: -120, y: -105, deg: 0 },  // 新建对话
      { x: -130, y:  -30, deg: 0 },  // 记录记忆
      { x: -130, y:   30, deg: 0 },  // 总结内容
      { x: -120, y:  105, deg: 0 },  // 翻译选中
      { x:  -85, y:  180, deg: 0 },  // 截图提问 (bottom)
    ];
    return positions;
  }, []);

  return (
    <div
      ref={containerRef}
      className="fixed z-[9999]"
      style={{
        left: pos.x, top: pos.y,
        transform: 'translate(-50%, -50%)',
        transition: isDragging ? 'none' : `left ${SNAP_ANIM_MS}ms ease-out, top ${SNAP_ANIM_MS}ms ease-out`,
      }}
    >
      {/* ── 长按轮盘菜单 ── */}
      <AnimatePresence>
        {showRadial && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0"
            style={{ pointerEvents: 'none' }}
          >
            {RADIAL_ITEMS.map((item, i) => {
              const angle = radialAngles[i];
              return (
                <motion.button
                  key={item.id}
                  data-radial-item
                  initial={{ opacity: 0, x: 0, y: 0, scale: 0.3 }}
                  animate={{
                    opacity: 1,
                    x: angle.x,
                    y: angle.y,
                    scale: hoveredRadial === item.id ? 1.15 : 1,
                  }}
                  exit={{ opacity: 0, x: 0, y: 0, scale: 0.3, transition: { duration: 0.15 } }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20, delay: i * 0.03 }}
                  onPointerEnter={() => setHoveredRadial(item.id)}
                  onPointerLeave={() => setHoveredRadial(null)}
                  onClick={() => handleRadialAction(item.id)}
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-2xl flex flex-col items-center justify-center gap-0.5 cursor-pointer group pointer-events-auto"
                  style={{ backgroundColor: item.color + '20', border: `1px solid ${item.color}40`, zIndex: 61 }}
                >
                  <span style={{ color: item.color }}>
                    {item.icon}
                  </span>
                  <span className="text-[9px] text-white/60 font-bold whitespace-nowrap">{item.label}</span>
                </motion.button>
              );
            })}
          </motion.div>
        )}

        {/* ── 遮挡背景（轮盘打开时） ── */}
        {showRadial && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[59] pointer-events-auto"
            onClick={() => setShowRadial(false)}
          />
        )}

        {/* ── 聊天面板 ── */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, x: 20 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.8, x: 20 }}
            className="absolute right-full top-1/2 -translate-y-1/2 mr-4 w-[min(340px,92vw)] sm:w-[420px]"
          >
            <GlassCard className={`flex flex-col max-h-[min(580px,85vh)] max-h-[min(580px,85vh)] max-h-[min(580px,85vh)] h-[580px] overflow-hidden ${theme.border} shadow-[0_20px_50px_rgba(0,0,0,0.5),0_0_30px_rgba(255,204,0,0.1)]`}>
              {/* Header */}
              <div className="p-4 border-b border-white/10 flex items-center justify-between bg-white/5 backdrop-blur-md">
                <div className="flex items-center gap-2.5">
                  <div className={`w-9 h-9 rounded-xl ${theme.iconBg} flex items-center justify-center text-black shadow-[0_0_15px_rgba(255,204,0,0.4)]`}>
                    <Bot size={18} />
                  </div>
                  <div>
                    <div className="text-sm font-bold tracking-tight">{t.userManual || '用户手册'}</div>
                    <div className={`text-[10px] ${theme.accentText} flex items-center gap-1 font-bold uppercase tracking-widest`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${theme.iconBg} animate-pulse`} />
                      {t.manualAssistant || '手册助手'}
                    </div>
                  </div>
                </div>
                <button onClick={() => setIsOpen(false)} className="w-7 h-7 rounded-full flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-all">
                  <X size={16} />
                </button>
              </div>

              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-5 scrollbar-hide bg-black/20">
                {messages.map((msg, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] p-3 rounded-2xl text-sm leading-relaxed ${msg.role === 'user'
                      ? 'bg-celestial-saturn text-black font-medium rounded-tr-none shadow-lg'
                      : 'bg-white/5 border border-white/10 text-white/90 rounded-tl-none backdrop-blur-sm'}`}>
                      <div className="markdown-body text-sm"><Markdown>{msg.content}</Markdown></div>
                    </div>
                  </motion.div>
                ))}
                {!isLoading && messages.length === 1 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3 pt-2">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/55 font-bold px-1">
                      <HelpCircle size={11} />{t.quickGuide || '快速指引'}
                    </div>
                    <div className="grid gap-2">
                      {quickSuggestions.map(s => (
                        <button key={s.id} onClick={() => handleSend(s.label)}
                          className={`flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10 text-xs text-white/60 hover:${theme.accentText} hover:border-white/20 hover:bg-white/[0.06] transition-all text-left group`}>
                          {s.label}<ChevronRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
                {isLoading && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                    <div className="bg-white/5 border border-white/10 p-3 rounded-2xl rounded-tl-none">
                      <div className="flex gap-1">
                        {[0, 0.2, 0.4].map((delay, i) => (
                          <motion.div key={i} animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay }}
                            className="w-1.5 h-1.5 bg-celestial-saturn rounded-full" />
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Input */}
              <div className="p-4 border-t border-white/10 bg-white/5 backdrop-blur-md">
                <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex gap-2.5">
                  <Button type="button" onClick={toggleListen}
                    className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all ${isListening ? 'bg-red-500 text-white animate-pulse shadow-[0_0_15px_rgba(239,68,68,0.4)]' : 'bg-white/5 text-white/40 hover:bg-white/10 border border-white/10'}`}>
                    {isListening ? <Mic size={18} /> : <MicOff size={18} />}
                  </Button>
                  <div className="relative flex-1">
                    <Input value={input} onChange={e => setInput(e.target.value)} placeholder={smartPlaceholder}
                      className={`h-10 bg-black/40 border-white/10 focus:border-${page === 'chat' ? 'celestial-saturn' : page === 'knowledge' ? 'indigo' : page === 'personality' ? 'violet' : 'celestial-saturn'}/50 rounded-2xl pr-10 text-sm`} />
                    <button type="submit" disabled={isLoading || !input.trim()}
                      className={`absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center ${theme.accentText} disabled:text-white/45 transition-colors`}>
                      <Send size={16} />
                    </button>
                  </div>
                </form>
              </div>
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── 上下文感知触发按钮 ── */}
      <motion.button
        data-drag-handle
        whileHover={{ scale: showRadial ? 1 : 1.1 }}
        whileTap={{ scale: 0.95 }}
        onPointerDown={handlePointerDown}
        onClick={() => {
          if (!isDragging && !isLongPress.current && !showRadial) setIsOpen(!isOpen);
          isLongPress.current = false;
        }}
        className={`w-14 h-14 rounded-full ${theme.bg} text-black ${theme.glow} flex items-center justify-center relative group overflow-hidden select-none`}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        {/* 抓握指示符 */}
        <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 flex gap-0.5 opacity-0 group-hover:opacity-40 transition-opacity">
          <div className="w-3 h-0.5 rounded-full bg-black/40" />
          <div className="w-3 h-0.5 rounded-full bg-black/40" />
        </div>
        {/* 图标 */}
        {isOpen ? <X size={24} /> : <HelpCircle size={24} />}
        {/* 未读标记 */}
        {!isOpen && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-celestial-mars rounded-full border-2 border-celestial-deep flex items-center justify-center text-[11px] font-bold text-white">1</div>
        )}
        {/* 长按提示 */}
        {!showRadial && !dragTooltip && !isOpen && (
          <div className="absolute right-full mr-3 px-3 py-2 bg-celestial-deep/95 backdrop-blur-xl border border-white/10 rounded-2xl text-[11px] font-bold text-white opacity-0 group-hover:opacity-100 transition-all whitespace-nowrap pointer-events-none shadow-2xl">
            <div className="flex items-center gap-1.5">
              <GripVertical size={12} className="text-white/50" />
              <span>长按打开快捷菜单</span>
            </div>
          </div>
        )}
        {/* 拖动提示 */}
        {dragTooltip && !isOpen && !showRadial && (
          <div className="absolute right-full mr-3 px-3 py-2 bg-celestial-deep/95 backdrop-blur-xl border border-white/10 rounded-2xl text-[11px] font-bold text-white opacity-0 group-hover:opacity-100 transition-all whitespace-nowrap pointer-events-none shadow-2xl">
            <div className="flex items-center gap-1.5">
              <GripVertical size={12} className="text-white/50" />
              <span>按住拖动到任意位置</span>
            </div>
          </div>
        )}
      </motion.button>
    </div>
  );
}
